/**
 * Reading time, as a pure function of the post's stored text length.
 *
 * Design: `designs/01-now/2026-09-06-read-surface-completion-reading-time-and-author-stats-design.md` §3
 * (round-6 D5 / A4). The length is the server's generated `discussions.body_text_length` column, delivered
 * on the DTO as `bodyTextLength`; nothing client-side measures the body, so the label can never disagree
 * with the composer's own count or with itself between the card and the post page.
 *
 * ONE computation site, used by both render surfaces (`ReadingTimeLabel`). A second site is the defect.
 */

/** A4: characters per minute. The design's own median post (1,531 chars) is 1.2 minutes at this rate. */
export const READING_TIME_CHARS_PER_MINUTE = 1275;

/**
 * Whole minutes to show, or `null` when nothing should be shown.
 *
 * D5: rounded to the nearest minute and HIDDEN under one. There is no "1 min read" floor: a figure below a
 * minute is noise, and a two-line post shows nothing. An absent, non-numeric, non-finite or negative input
 * (an older list shape without the field, a malformed attribute) is also `null`: fail closed, never
 * `NaN min read`.
 */
export function readingTimeMinutes(bodyTextLength: number | null | undefined): number | null {
    if (typeof bodyTextLength !== "number" || !Number.isFinite(bodyTextLength) || bodyTextLength < 0) {
        return null;
    }
    const minutes = Math.round(bodyTextLength / READING_TIME_CHARS_PER_MINUTE);
    return minutes >= 1 ? minutes : null;
}
